import { Link } from "@tanstack/react-router";
import { useEffect, useId, useRef, useState } from "react";
import { ChevronDown, Menu, Phone, X } from "lucide-react";
import { ResponsiveImage } from "@/components/ResponsiveImage";
import { logoImage } from "@/lib/images";
import { NAV, SITE } from "@/lib/site";

export function SiteHeader() {
  const [open, setOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const servicesWrapRef = useRef<HTMLDivElement>(null);
  const servicesButtonRef = useRef<HTMLButtonElement>(null);
  const servicesMenuRef = useRef<HTMLUListElement>(null);
  const servicesMenuId = useId();
  const mobileServicesId = useId();

  useEffect(() => {
    if (!servicesOpen) return;

    const firstItem =
      servicesMenuRef.current?.querySelector<HTMLAnchorElement>('[role="menuitem"]');
    firstItem?.focus();

    function onPointerDown(event: MouseEvent) {
      if (!servicesWrapRef.current?.contains(event.target as Node)) {
        setServicesOpen(false);
      }
    }

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        setServicesOpen(false);
        servicesButtonRef.current?.focus();
        return;
      }

      const items = Array.from(
        servicesMenuRef.current?.querySelectorAll<HTMLAnchorElement>('[role="menuitem"]') ?? [],
      );
      if (items.length === 0) return;
      const index = items.indexOf(document.activeElement as HTMLAnchorElement);

      if (event.key === "ArrowDown") {
        event.preventDefault();
        items[(index + 1) % items.length]?.focus();
      } else if (event.key === "ArrowUp") {
        event.preventDefault();
        items[(index - 1 + items.length) % items.length]?.focus();
      } else if (event.key === "Home") {
        event.preventDefault();
        items[0]?.focus();
      } else if (event.key === "End") {
        event.preventDefault();
        items[items.length - 1]?.focus();
      }
    }

    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [servicesOpen]);

  function closeMobile() {
    setOpen(false);
    setMobileServicesOpen(false);
  }

  return (
    <header className="sticky top-0 z-50 border-b border-border bg-secondary text-secondary-foreground">
      <div className="mx-auto flex h-[4.5rem] max-w-7xl items-center justify-between px-4 lg:px-8">
        <Link to="/" className="flex items-center gap-3">
          <ResponsiveImage
            {...logoImage}
            alt={`${SITE.shortName} logo`}
            className="h-12 w-12 rounded-full ring-2 ring-accent"
            loading="eager"
            fetchPriority="low"
          />
          <div className="leading-tight">
            <div className="font-display text-lg tracking-wider text-accent">{SITE.shortName}</div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-secondary-foreground/70">
              {SITE.tagline}
            </div>
          </div>
        </Link>

        <nav className="hidden items-center gap-8 lg:flex" aria-label="Primary">
          <div className="relative" ref={servicesWrapRef}>
            <button
              ref={servicesButtonRef}
              type="button"
              className="inline-flex items-center gap-1 font-display text-sm tracking-widest text-secondary-foreground/80 hover:text-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
              aria-expanded={servicesOpen}
              aria-haspopup="true"
              aria-controls={servicesMenuId}
              onClick={() => setServicesOpen((v) => !v)}
              onKeyDown={(event) => {
                if (event.key === "ArrowDown" && !servicesOpen) {
                  event.preventDefault();
                  setServicesOpen(true);
                }
              }}
            >
              Services
              <ChevronDown
                className={`h-4 w-4 transition-transform ${servicesOpen ? "rotate-180" : ""}`}
                aria-hidden="true"
              />
            </button>
            {servicesOpen && (
              <ul
                ref={servicesMenuRef}
                id={servicesMenuId}
                role="menu"
                aria-label="Services"
                className="absolute left-0 top-full z-50 mt-3 min-w-[16rem] rounded-md border border-border bg-secondary py-2 shadow-lg"
              >
                {NAV.services.map((item) => (
                  <li key={item.to} role="none">
                    <Link
                      role="menuitem"
                      to={item.to}
                      className="block px-4 py-2.5 font-display text-sm tracking-wider text-secondary-foreground/90 hover:bg-secondary-foreground/10 hover:text-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent"
                      onClick={() => setServicesOpen(false)}
                    >
                      {item.label}
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {NAV.primary.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              className="font-display text-sm tracking-widest text-secondary-foreground/80 hover:text-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
            >
              {item.label}
            </Link>
          ))}
        </nav>

        <a
          href={`tel:${SITE.phoneTel}`}
          className="hidden items-center gap-2 rounded-md bg-primary px-4 py-2 font-display tracking-wider text-primary-foreground hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent lg:inline-flex"
        >
          <Phone className="h-4 w-4" aria-hidden="true" /> {SITE.phoneDisplay}
        </a>

        <button
          type="button"
          onClick={() => setOpen(!open)}
          className="min-h-11 min-w-11 rounded-md p-2 text-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent lg:hidden"
          aria-label={open ? "Close menu" : "Open menu"}
          aria-expanded={open}
          aria-controls="mobile-navigation"
        >
          {open ? <X aria-hidden="true" /> : <Menu aria-hidden="true" />}
        </button>
      </div>

      {open && (
        <nav
          id="mobile-navigation"
          className="border-t border-border bg-secondary lg:hidden"
          aria-label="Mobile"
        >
          <div className="flex flex-col px-4 py-4">
            <div className="border-b border-border/40">
              <button
                type="button"
                className="flex min-h-11 w-full items-center justify-between py-3 font-display tracking-wider text-secondary-foreground/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                aria-expanded={mobileServicesOpen}
                aria-controls={mobileServicesId}
                onClick={() => setMobileServicesOpen((v) => !v)}
              >
                Services
                <ChevronDown
                  className={`h-4 w-4 transition-transform ${mobileServicesOpen ? "rotate-180" : ""}`}
                  aria-hidden="true"
                />
              </button>
              {mobileServicesOpen && (
                <ul id={mobileServicesId} className="mb-2 space-y-1 pb-2 pl-3">
                  {NAV.services.map((item) => (
                    <li key={item.to}>
                      <Link
                        to={item.to}
                        onClick={closeMobile}
                        className="flex min-h-11 items-center font-display tracking-wider text-secondary-foreground/80 hover:text-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                      >
                        {item.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {NAV.primary.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                onClick={closeMobile}
                className="min-h-11 border-b border-border/40 py-3 font-display tracking-wider text-secondary-foreground/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
              >
                {item.label}
              </Link>
            ))}

            <a
              href={`tel:${SITE.phoneTel}`}
              className="mt-4 min-h-11 rounded-md bg-primary px-4 py-3 text-center font-display tracking-wider text-primary-foreground"
            >
              Call {SITE.phoneDisplay}
            </a>
          </div>
        </nav>
      )}
    </header>
  );
}
